/**
 * RefinePanel — the one-box page's filters, built FROM THE RESULTS.
 *
 * The old panel asked the operator to decide what they were looking for before they searched:
 * All / Person / Vehicle, then groups, cameras, model types, a name box, a plate box. This one
 * comes after. The box finds everything a sentence could mean; this panel lists what is actually
 * in that answer, with a count beside each value, and the operator ticks away what they did not
 * want. Nothing is offered that would return zero, except a value already ticked (so it can be
 * unticked).
 *
 * The counts come from `POST /search/refine`, which counts each dimension with every OTHER
 * refinement applied but its own — so ticking "Red" leaves "White 40" beside it, ready to be
 * ticked too. Values within a group are OR'd; groups are AND'd. A section with nothing in the
 * results is not drawn at all: a search for "truck" offers vehicle colours, not shirt colours.
 */
import type { ReactNode } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type {
  SearchEventType, SearchFacetValue, SearchFuzziness, SearchMatchGroup, SearchMatchReport, SearchRefine,
} from '../../api/contracts';
import { Swatch } from './FilterControls';
import { COLOR_FIELDS, FIELD_LABEL, MODEL_LABEL, attrWord } from './searchModel';

/** How each event type reads in the panel and in its chip — the names operators use. */
export const TYPE_LABEL: Record<string, string> = {
  face: 'Face recognition (FRS)',
  person: 'Person detection',
  vehicle_crossing: 'Vehicle / ANPR',
  ocr_plate: 'Number plate (OCR)',
  ocr_text: 'Text read (OCR)',
};
export const typeLabel = (t: string) => TYPE_LABEL[t] || MODEL_LABEL[t] || t;
/** The five search tables first, in the order the operator thinks of them; the rest after. */
const TYPE_ORDER = ['face', 'person', 'vehicle_crossing', 'ocr_plate', 'ocr_text'];
const typeRank = (t: string) => (TYPE_ORDER.includes(t) ? TYPE_ORDER.indexOf(t) : TYPE_ORDER.length);

const SPELLING: Array<{ value: SearchFuzziness; label: string; hint: string }> = [
  { value: 'exact', label: 'Exact', hint: 'Only the word as typed (and words starting with it)' },
  { value: 'close', label: 'Close', hint: 'Also one-letter slips — rajn finds rajan' },
  { value: 'loose', label: 'Loose', hint: 'Also sound-alikes and misreads — jai finds jay, badal finds vadal' },
];

/** The attribute sections, grouped by what they describe. */
const PERSON_FIELDS = ['upper_color', 'lower_color', 'gender', 'age_band', 'accessories'];
const VEHICLE_FIELDS = ['vehicle_type', 'vehicle_color', 'direction'];

/** Longer names for the person colour fields here — "Upper" alone reads oddly as a heading. */
const SECTION_LABEL: Record<string, string> = {
  upper_color: 'Top colour',
  lower_color: 'Bottom colour',
  vehicle_type: 'Vehicle type',
  vehicle_color: 'Vehicle colour',
};

/**
 * A long list shows TWO rows and scrolls for the rest — the panel has a dozen sections, and a
 * thirty-camera list pushed every one under it off the screen. The count of what is below the
 * fold is said under the box, so a scrolled-away row is never a hidden one.
 */
function ScrollList({ count, children }: { count: number; children: ReactNode }) {
  return (
    <>
      <div className={`space-y-0.5 ${count > 2 ? 'max-h-[4.4rem] overflow-y-auto rounded-md border border-border p-0.5 pr-1' : ''}`}>
        {children}
      </div>
      {count > 2 && (
        <p className="px-2 text-[11px] text-muted-foreground">{count - 2} more — scroll the list</p>
      )}
    </>
  );
}

/** The panel's own name for a field — the active-filter chips use it too, so the two agree. */
export const refineFieldLabel = (field: string) => SECTION_LABEL[field] || FIELD_LABEL[field] || field;

export interface RefinePanelProps {
  refine: SearchRefine | null;
  /** The counts are being recomputed — shown faintly rather than blanked. */
  loading: boolean;
  /** Event types removed from the results. Everything else is shown (ticked). */
  hiddenTypes: SearchEventType[];
  fuzziness: SearchFuzziness;
  cameras: string[];
  attributes: Record<string, string[]>;
  watchlist: 'any' | 'listed' | 'not_listed';
  cameraName: (id: string) => string | undefined;
  onHiddenTypes: (types: SearchEventType[]) => void;
  onFuzziness: (f: SearchFuzziness) => void;
  onCameras: (ids: string[]) => void;
  onAttribute: (field: string, values: string[]) => void;
  onWatchlist: (value: 'any' | 'listed' | 'not_listed') => void;
}

/** Selected values that the counts no longer list still appear — at 0 — so they can be unticked. */
function withSelected(values: SearchFacetValue[], selected: string[]): SearchFacetValue[] {
  const have = new Set(values.map((v) => v.value));
  return [...values, ...selected.filter((s) => !have.has(s)).map((value) => ({ value, events: 0 }))];
}

const toggle = (list: string[], value: string) => (list.includes(value)
  ? list.filter((x) => x !== value)
  : [...list, value]);

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/** One tickable value with its count — the row shape every list section uses. */
function CheckRow({
  label, count, on, onClick, adornment,
}: { label: string; count: number; on: boolean; onClick: () => void; adornment?: ReactNode }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors ${
        on ? 'bg-brand-light text-foreground' : 'text-foreground hover:bg-muted'
      } ${count === 0 && !on ? 'opacity-50' : ''}`}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
          on ? 'border-brand bg-brand text-white' : 'border-border bg-card'
        }`}
      >
        {on && <CheckIcon className="h-3 w-3 stroke-[3]" />}
      </span>
      {adornment}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="shrink-0 tabular-nums text-[12px] text-muted-foreground">{count.toLocaleString()}</span>
    </button>
  );
}

/** A colour field as chips with a swatch — colours are recognised before they are read. */
function ColourChips({
  values, selected, onChange,
}: { values: SearchFacetValue[]; selected: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v) => {
        const on = selected.includes(v.value);
        return (
          <button
            key={v.value}
            type="button"
            role="checkbox"
            aria-checked={on}
            onClick={() => onChange(toggle(selected, v.value))}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
              on ? 'border-brand bg-brand-light font-medium text-foreground' : 'border-border bg-card text-foreground hover:bg-muted'
            } ${v.events === 0 && !on ? 'opacity-50' : ''}`}
          >
            <Swatch value={v.value} className="h-3 w-3" />
            {attrWord(v.value)}
            <span className="tabular-nums text-muted-foreground">{v.events.toLocaleString()}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function RefinePanel({
  refine, loading, hiddenTypes, fuzziness, cameras, attributes, watchlist, cameraName,
  onHiddenTypes, onFuzziness, onCameras, onAttribute, onWatchlist,
}: RefinePanelProps) {

  /** How closely a typed word must be spelt — always shown, it shapes the results themselves. */
  const spelling = (
    <Section title="Spelling">
      <div data-tour="search-spelling" className="grid grid-cols-3 overflow-hidden rounded-lg border border-border" role="radiogroup" aria-label="Spelling">
        {SPELLING.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={fuzziness === o.value}
            title={o.hint}
            onClick={() => onFuzziness(o.value)}
            className={`px-2 py-1.5 text-[12px] font-medium transition-colors ${
              fuzziness === o.value ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {SPELLING.find((o) => o.value === fuzziness)?.hint}
      </p>
    </Section>
  );

  if (!refine) {
    return (
      <div className="space-y-5">
        {spelling}
        <p className="text-[12px] text-muted-foreground">{loading ? 'Counting what was found…' : 'Search to see what you can narrow by.'}</p>
      </div>
    );
  }
  if (refine.omitted) {
    return (
      <div className="space-y-5">
        {spelling}
        <p data-tour="search-refine-notice" className="text-[12px] leading-snug text-amber-500">
          Too many results to count by type, camera and appearance. Add a word or narrow the time window
          and the choices come back.
        </p>
      </div>
    );
  }

  // Every type in the results, ticked unless removed — and a removed type stays listed at its
  // count, so it can be put back.
  const typeValues = withSelected(refine.types, hiddenTypes)
    .sort((a, b) => typeRank(a.value) - typeRank(b.value) || b.events - a.events);
  // Ticked cameras first, so the two rows above the fold are the ones narrowing the answer.
  const cameraValues = withSelected(refine.cameras, cameras)
    .sort((a, b) => Number(cameras.includes(b.value)) - Number(cameras.includes(a.value)));

  const attrSection = (field: string) => {
    const selected = attributes[field] || [];
    // `unknown` is the model saying it could not tell — a real value, but listed last.
    const values = withSelected(refine.attributes?.[field] || [], selected)
      .sort((a, b) => Number(a.value === 'unknown') - Number(b.value === 'unknown'));
    if (!values.length) return null;
    const title = refineFieldLabel(field);
    return (
      <Section key={field} title={title}>
        {COLOR_FIELDS.has(field) ? (
          <ColourChips values={values} selected={selected} onChange={(v) => onAttribute(field, v)} />
        ) : (
          <div className="space-y-0.5">
            {values.map((v) => (
              <CheckRow
                key={v.value}
                label={attrWord(v.value)}
                count={v.events}
                on={selected.includes(v.value)}
                onClick={() => onAttribute(field, toggle(selected, v.value))}
              />
            ))}
          </div>
        )}
      </Section>
    );
  };

  const personSections = PERSON_FIELDS.map(attrSection).filter(Boolean);
  const vehicleSections = VEHICLE_FIELDS.map(attrSection).filter(Boolean);
  const listedCount = (v: string) => refine.watchlist.find((w) => w.value === v)?.events || 0;

  return (
    <div className={`space-y-5 transition-opacity ${loading ? 'opacity-60' : ''}`}>
      {spelling}

      {typeValues.length > 0 && (
        <div data-tour="search-refine-types">
        <Section title="Event types">
          <ScrollList count={typeValues.length}>
            {typeValues.map((t) => (
              <CheckRow
                key={t.value}
                label={typeLabel(t.value)}
                count={t.events}
                on={!hiddenTypes.includes(t.value)}
                onClick={() => onHiddenTypes(toggle(hiddenTypes, t.value))}
              />
            ))}
          </ScrollList>
        </Section>
        </div>
      )}

      {cameraValues.length > 0 && (
        <div data-tour="search-refine-cameras">
        <Section title="Seen on">
          <ScrollList count={cameraValues.length}>
            {cameraValues.map((c) => (
              <CheckRow
                key={c.value}
                label={cameraName(c.value) || c.value.slice(0, 8)}
                count={c.events}
                on={cameras.includes(c.value)}
                onClick={() => onCameras(toggle(cameras, c.value))}
              />
            ))}
          </ScrollList>
        </Section>
        </div>
      )}

      {personSections.length > 0 && (
        <div data-tour="search-refine-people" className="space-y-4 rounded-lg border border-border p-3">
          <p className="text-[12px] font-semibold text-foreground">People</p>
          {personSections}
        </div>
      )}

      {vehicleSections.length > 0 && (
        <div data-tour="search-refine-vehicles" className="space-y-4 rounded-lg border border-border p-3">
          <p className="text-[12px] font-semibold text-foreground">Vehicles</p>
          {vehicleSections}
        </div>
      )}

      {(refine.watchlist.length > 0 || watchlist !== 'any') && (
        <div data-tour="search-refine-watchlist">
        <Section title="Watch list">
          <div className="space-y-0.5">
            <CheckRow
              label="On a watch list"
              count={listedCount('listed')}
              on={watchlist === 'listed'}
              onClick={() => onWatchlist(watchlist === 'listed' ? 'any' : 'listed')}
            />
            <CheckRow
              label="Not on any list"
              count={listedCount('not_listed')}
              on={watchlist === 'not_listed'}
              onClick={() => onWatchlist(watchlist === 'not_listed' ? 'any' : 'not_listed')}
            />
          </div>
        </Section>
        </div>
      )}
    </div>
  );
}

// ─── "Understood" — one typed word and what it is searched as ──────────────

/** What an attribute reading of a word is, in the chip. */
const READING_LABEL: Record<string, string> = {
  upper_color: 'top',
  lower_color: 'bottom',
  vehicle_color: 'vehicle',
  vehicle_type: 'vehicle type',
  gender: 'gender',
  age_band: 'age',
  accessories: 'carrying',
  direction: 'direction',
};

/**
 * One word of the sentence, with every reading it is being searched as — "red → top · bottom ·
 * vehicle · on signs" — each with an × to drop it. A text reading lists the spellings the server
 * matched, so a fuzzy match is never silent, and says plainly when the word matched nothing.
 */
export function MatchWordChip({
  group, report, onDrop,
}: { group: SearchMatchGroup; report: SearchMatchReport | null; onDrop: (reading: number) => void }) {
  // Values are named only when the word carries more than one ("red or blue shirt").
  const values = new Set(group.any.flatMap((a) => ('attr' in a ? a.values : [])));
  const named = values.size > 1;

  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-1 rounded-xl border border-border bg-card px-3 py-1 text-[13px] shadow-sm">
      <span className="font-semibold text-foreground">{group.word}</span>
      <span className="text-muted-foreground">→</span>
      {group.any.map((a, i) => {
        let label: string;
        let detail: ReactNode = null;
        if ('attr' in a) {
          label = READING_LABEL[a.attr] || a.attr;
          if (named) label = `${label} ${a.values.map(attrWord).join('/')}`;
        } else {
          label = a.whole_word ? 'on signs' : 'names, plates & text';
          if (a.whole_word && named) label = `“${a.text}” on signs`;
          // The spellings and people a lookup matched are listed — strikable — in the bar below;
          // here only the one thing worth saying in a chip: that it found nothing.
          const r = report?.readings.find((x) => x.type === 'text' && x.q.toLowerCase() === a.text.toLowerCase());
          if (r && r.type === 'text' && r.unmatched) detail = <span className="text-amber-500">no match</span>;
        }
        return (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[12px] text-foreground">
            {label}
            {detail && <span className="text-[11px]">· {detail}</span>}
            <button
              type="button"
              onClick={() => onDrop(i)}
              aria-label={`Do not search “${group.word}” as ${label}`}
              title={`Do not search “${group.word}” as ${label}`}
              className="text-muted-foreground hover:text-foreground"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        );
      })}
    </span>
  );
}
